// MCC Event Studio — shared frontend vocabulary.
//
// These MIRROR backend/events.py, backend/legal_policies.py and
// backend/brochures.py. The server is the source of truth and revalidates
// everything; this file only drives the UI so we never offer a status,
// transition or consent value the backend would reject.

import { badgeClass, formatDate, formatDateTime } from './applications';
import { NOT_PROVIDED, displayValue } from './websiteEngagement';

export { badgeClass, formatDate, formatDateTime, displayValue, NOT_PROVIDED };

export const EVENT_STATUS_META = {
  draft: { label: 'Draft', tone: 'neutral', pillTone: 'neutral' },
  review: { label: 'In review', tone: 'warning', pillTone: 'gold' },
  published: { label: 'Published', tone: 'success', pillTone: 'emerald' },
  closed: { label: 'Registration closed', tone: 'warning', pillTone: 'amber' },
  completed: { label: 'Completed', tone: 'neutral', pillTone: 'slate' },
  archived: { label: 'Archived', tone: 'neutral', pillTone: 'slate' },
};

export const EVENT_STATUSES = Object.keys(EVENT_STATUS_META);

export const ATTENDANCE_MODES = [
  { value: 'online', label: 'Online' },
  { value: 'in_person', label: 'In person' },
  { value: 'hybrid', label: 'Hybrid (online and in person)' },
];

export const EVENT_LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'zh', label: '中文' },
];

export const POLICY_TYPES = [
  { value: 'privacy', label: 'Privacy Policy' },
  { value: 'terms', label: 'Website Terms and Conditions' },
  { value: 'event_terms', label: 'Event Terms' },
  { value: 'media_release', label: 'Media Release' },
  { value: 'marketing_consent', label: 'Marketing Consent' },
];

export const POLICY_STATUS_META = {
  draft: { label: 'Draft', pillTone: 'neutral' },
  review: { label: 'In review', pillTone: 'gold' },
  published: { label: 'Published', pillTone: 'emerald' },
  retired: { label: 'Retired', pillTone: 'slate' },
};

export const CONSENT_VALUES = [
  { value: 'consent', label: 'Consented' },
  { value: 'no_consent', label: 'Declined' },
];

// ─── Route states ───
// What the marketing site can actually serve. "Verified" is evidence, not a
// setting: only a live read of the website may claim it, so the UI never offers
// it as something to pick.
export const ROUTE_STATUS_META = {
  not_checked: { label: 'Not checked', pillTone: 'neutral' },
  draft: { label: 'Draft', pillTone: 'neutral' },
  needs_website_setup: { label: 'Needs website setup', pillTone: 'gold' },
  verified: { label: 'Verified', pillTone: 'emerald' },
  unreachable: { label: 'Unreachable', pillTone: 'red' },
  retired: { label: 'Retired', pillTone: 'slate' },
};

export const ROUTE_STATUS_OPTIONS = ['not_checked', 'draft', 'needs_website_setup', 'retired'];

export const ROUTE_DESTINATION_TYPES = [
  { value: 'event_page', label: 'Event detail page' },
  { value: 'program_page', label: 'Related program page' },
  { value: 'site_page', label: 'Another MCC website page' },
  { value: 'external_url', label: 'Approved external URL' },
];

export function routeStatusMeta(status) {
  return ROUTE_STATUS_META[status] || { label: status || 'Unknown', pillTone: 'neutral' };
}

export function routeDestinationLabel(value) {
  const item = ROUTE_DESTINATION_TYPES.find((option) => option.value === value);
  return item ? item.label : displayValue(value);
}

// ─── Staff reference code ───
// A descriptive internal label — "fsl-trial-2026-09-01" — that helps staff
// recognise an event by type and date. Never a foreign key, never public.
// MIRRORS backend/events.py; the server revalidates everything below.
export const STAFF_REFERENCE_MIN_LEN = 3;
export const STAFF_REFERENCE_MAX_LEN = 100;
const STAFF_REFERENCE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const RESERVED_STAFF_REFERENCE_CODES = [
  'new', 'edit', 'all', 'none', 'null', 'undefined', 'true', 'false',
  'admin', 'api', 'event', 'events', 'registration', 'registrations',
  'policies', 'policy', 'brochure', 'brochures', 'preview', 'index',
  'search', 'unlinked', 'test', 'default',
];

/**
 * Trim and lowercase, and nothing else. Case folds because uniqueness is
 * case-insensitive; spaces are left in so the validator can refuse them rather
 * than quietly saving an identifier nobody typed.
 */
export function normalizeStaffReferenceCode(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function staffReferenceCodeErrors(value) {
  const code = normalizeStaffReferenceCode(value);
  if (!code) return ['A staff reference code is required.'];
  const errors = [];
  if (code.length < STAFF_REFERENCE_MIN_LEN || code.length > STAFF_REFERENCE_MAX_LEN) {
    errors.push(`The staff reference code must be ${STAFF_REFERENCE_MIN_LEN}–${STAFF_REFERENCE_MAX_LEN} characters.`);
  }
  if (!STAFF_REFERENCE_PATTERN.test(code)) {
    errors.push('Lowercase letters, numbers and single hyphens only — no spaces or underscores.');
  }
  if (RESERVED_STAFF_REFERENCE_CODES.includes(code)) {
    errors.push(`“${code}” is reserved and cannot be used.`);
  }
  return errors;
}

/** "FSL Info & Trial Session" + 2026-09-01 → "fsl-info-trial-session-2026-09-01". */
export function suggestStaffReferenceCode(name, date = '') {
  const stem = String(name || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // fold accents so "Seance" slugs readably
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/, '');
  const day = /^\d{4}-\d{2}-\d{2}$/.test(String(date || '').slice(0, 10))
    ? String(date).slice(0, 10) : '';
  const code = [stem, day].filter(Boolean).join('-');
  return staffReferenceCodeErrors(code).length === 0 ? code : '';
}

// ─── Public path ───
export function normalizePublicPath(value) {
  let text = String(value || '').trim();
  if (!text) return '';
  if (!text.startsWith('/')) text = `/${text}`;
  while (text.includes('//')) text = text.replace('//', '/');
  return text.length > 1 ? text.replace(/\/+$/, '') : text;
}

/**
 * The scheme and host of a URL, with none of its path.
 *
 * The editor's URL field is `origin + path`, so an "origin" that still carries
 * a path re-adds that path on every keystroke. Everything that builds a public
 * URL goes through here so it cannot.
 */
export function publicOriginOf(value) {
  const match = String(value || '').trim().match(/^(https?:\/\/[^/?#]+)/i);
  return match ? match[1] : '';
}

export function publicUrlFor(origin, path) {
  const text = String(origin || '').replace(/\/+$/, '');
  const base = publicOriginOf(text) || text;
  const suffix = normalizePublicPath(path);
  return base && suffix ? `${base}${suffix}` : '';
}

// The editor's tabs, in the order someone actually fills an event in.
export const EVENT_EDITOR_TABS = [
  { id: 'basics', label: 'Basics' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'content', label: 'Page Content' },
  { id: 'registration', label: 'Registration' },
  { id: 'brochures', label: 'Brochures' },
  { id: 'social', label: 'Social & SEO' },
  { id: 'agencies', label: 'Agency Links' },
  { id: 'legal', label: 'Legal & Consent' },
  { id: 'publish', label: 'Preview & Publish' },
];

export function eventStatusMeta(status) {
  return EVENT_STATUS_META[status]
    || { label: status || 'Unknown', tone: 'neutral', pillTone: 'neutral' };
}

export function policyStatusMeta(status) {
  return POLICY_STATUS_META[status] || { label: status || 'Unknown', pillTone: 'neutral' };
}

export function attendanceModeLabel(value) {
  const mode = ATTENDANCE_MODES.find((item) => item.value === value);
  return mode ? mode.label : displayValue(value);
}

export function localeLabel(value) {
  const locale = EVENT_LOCALES.find((item) => item.value === value);
  return locale ? locale.label : displayValue(value);
}

export function policyTypeLabel(value) {
  const type = POLICY_TYPES.find((item) => item.value === value);
  return type ? type.label : displayValue(value);
}

export function consentLabel(value) {
  const choice = CONSENT_VALUES.find((item) => item.value === value);
  return choice ? choice.label : 'Not answered';
}

/** The English title, which is the one staff recognise an event by. */
export function eventTitle(event) {
  const translations = event?.translations || {};
  return translations.en?.title || translations[event?.locale]?.title || event?.title || '';
}

/** A single-line "when", tolerant of an event with no dates yet. */
export function eventWhen(event) {
  const start = event?.start_at;
  if (!start) return NOT_PROVIDED;
  const startText = formatDateTime(start);
  if (!event?.end_at) return startText;
  const sameDay = String(start).slice(0, 10) === String(event.end_at).slice(0, 10);
  return sameDay
    ? `${startText} – ${new Date(event.end_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : `${startText} – ${formatDateTime(event.end_at)}`;
}

/** "12 of 40" or just the count when the event is uncapped. */
export function capacityLabel(event) {
  const registered = Number(event?.registration_count) || 0;
  const capacity = Number(event?.capacity) || 0;
  if (!capacity) return `${registered}`;
  return `${registered} of ${capacity}`;
}

/**
 * Whether this event has already happened — MIRRORS `is_past` in
 * backend/events.py, and prefers the server's answer when the payload carries
 * it. An event with no dates yet is unscheduled, not past.
 */
export function isPastEvent(event, now = new Date()) {
  if (typeof event?.is_past === 'boolean') return event.is_past;
  if (event?.status === 'completed') return true;
  const end = event?.end_at || event?.start_at;
  if (!end) return false;
  const moment = new Date(end);
  return Number.isNaN(moment.getTime()) ? false : moment < now;
}

/**
 * What deleting this event takes with it, as the server computed it. Every
 * event is deletable; the warnings are what has to be shown first.
 */
export function eventDeleteImpact(event) {
  const impact = event?.delete_impact || {};
  return {
    registrationCount: Number(impact.registration_count) || 0,
    wasPublished: !!impact.was_published,
    requiresConfirmation: !!impact.requires_confirmation,
    warnings: Array.isArray(impact.warnings) ? impact.warnings : [],
  };
}

export function seatsRemaining(event) {
  const capacity = Number(event?.capacity) || 0;
  if (!capacity) return null;
  return Math.max(0, capacity - (Number(event?.registration_count) || 0));
}

/**
 * Whether this event may move to `target`, mirroring the backend's table so the
 * UI never offers a button the server would refuse.
 */
export function canTransition(event, target) {
  return (event?.allowed_transitions || []).includes(target);
}

/** What still blocks publishing, as the server computed it. */
export function publishBlockers(event) {
  return Array.isArray(event?.publish_blockers) ? event.publish_blockers : [];
}

export function isPublishable(event) {
  return publishBlockers(event).length === 0 && canTransition(event, 'published');
}

/** A published policy is frozen; editing means creating a new version. */
export function isPolicyEditable(policy) {
  return ['draft', 'review'].includes(policy?.status);
}

/**
 * Build the empty locale content block the editor starts from, so a new
 * translation has every field present rather than appearing item by item.
 */
export function emptyTranslation() {
  return {
    title: '', eyebrow: '', summary: '', description_html: '',
    benefits: [], how_it_works: [], agenda: [], agenda_label: '', faqs: [],
    location_summary: '', seo_title: '', meta_description: '',
    social_title: '', social_summary: '',
  };
}

/**
 * Clean a program slug the way backend/event_programs.py does: lowercase,
 * accents folded, everything that is not a letter or a digit collapsed to a
 * single hyphen. Mirrored here only so the editor shows staff the value that
 * will actually be stored — the server normalizes again and has the last word.
 */
export function normalizeProgramSlug(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/^-+|-+$/g, '');
}

/** How many programs one event may be filed under. Mirrors MAX_EVENT_PROGRAMS. */
export const MAX_EVENT_PROGRAMS = 12;

/**
 * The options list with any slug already on the event folded in, so a tag typed
 * before it reached the catalog still renders as a chip rather than vanishing.
 */
export function programOptionsWithSelected(options, selected) {
  const list = Array.isArray(options) ? options : [];
  const known = new Set(list.map((option) => option.slug));
  const extra = (Array.isArray(selected) ? selected : [])
    .filter((slug) => slug && !known.has(slug))
    .map((slug) => ({ slug, label: slug, program_code: '', source: 'custom' }));
  return [...list, ...extra];
}

/** Group registrations by their consent answers, for the attendee summary. */
export function consentSummary(registrations = []) {
  const list = Array.isArray(registrations) ? registrations : [];
  return {
    total: list.length,
    noPhoto: list.filter((item) => item?.consent?.media_choice === 'no_consent').length,
    marketingOptIn: list.filter((item) => item?.consent?.marketing_choice === 'consent').length,
    minors: list.filter((item) => item?.consent?.is_minor).length,
  };
}
